import { useCallback } from 'react';

import { useShallow } from 'zustand/react/shallow';

import { getDisplayMediaStream, stopMediaStream } from '@/shared/lib/media-devices';

import { useDisplayMediaStore } from './display-media-store';

export function useDisplayMediaControls() {
    const { currentStream, _setCapturing, _setCaptured, _setFailed, _clear } = useDisplayMediaStore(
        useShallow((state) => ({
            currentStream: state.currentStream,
            _setCapturing: state._setCapturing,
            _setCaptured: state._setCaptured,
            _setFailed: state._setFailed,
            _clear: state._clear,
        })),
    );

    const capture = useCallback(async () => {
        if (currentStream) {
            return { success: true, accessDenied: false, stream: currentStream };
        }

        _setCapturing();

        const result = await getDisplayMediaStream();

        if (!result.success) {
            _setFailed(result.accessDenied);
            return { success: false, accessDenied: result.accessDenied };
        }

        const hasAudio = result.stream.getAudioTracks().length > 0;

        if (!hasAudio) {
            stopMediaStream(result.stream);
            _setFailed(false);
            return { success: false, accessDenied: false };
        }

        result.stream.getTracks().forEach((track) => {
            track.addEventListener('ended', _clear, { once: true });
        });

        _setCaptured(result.stream);

        return { success: true, accessDenied: false, stream: result.stream };
    }, [currentStream, _setCapturing, _setCaptured, _setFailed, _clear]);

    const clear = useCallback(() => {
        // Read the store directly, not `currentStream`: keeps `clear`'s identity stable so an
        // effect can depend on it, and stops the live stream, not the one captured at creation.
        const streamToStop = useDisplayMediaStore.getState().currentStream;
        stopMediaStream(streamToStop);
        _clear();
    }, [_clear]);

    return { capture, clear };
}
